import { WeatherDay } from '@models/weather.model';
import { renderWeatherGlyphHtml } from '@components/atoms/WeatherGlyph';
import { isToday as isCalendarToday } from '@utils/dateOnlyParser';
import { buildWeatherTooltipText, WeatherLocaleStrings } from './weatherContent';
import { PROJECT_VIEW } from '../utils/projectView';

/**
 * Sprint 34 regression fix (story-133). FullCalendar's `slotLabelDidMount`/`dayCellDidMount`
 * hooks only fire when a cell is *created* — not when `Calendar#render()` is called on an
 * already-mounted view. `useProjectWeather` resolves after first paint by design (never awaited
 * before first paint, per the story), so on first open every visible cell is typically already
 * mounted by the time weather data arrives, and those DidMount hooks never fire again for them.
 *
 * `applyWeatherGlyphs` closes that gap: it walks the calendar's own already-mounted DOM (never
 * `document` — several project accordions can each hold a mounted calendar instance at once) and
 * (re)attaches glyphs directly, independent of DidMount. The DidMount hooks remain the mechanism
 * for cells mounted *after* this runs (e.g. scrolling in more resource rows, or a fresh cell
 * created by prev/next navigation); this helper covers everything already on screen.
 */

export interface WeatherGlyphContext {
  weatherByDate: Map<string, WeatherDay>;
  strings: WeatherLocaleStrings;
  dateLocale: string;
}

const TIMELINE_SLOT_LABEL_SELECTOR = '.fc-timeline-slot-label[data-date]';
const DAY_GRID_CELL_SELECTOR = '.fc-daygrid-day[data-date]';

const TIMELINE_SLOT_LABEL_CONTENT_SELECTORS = [
  '.fc-col-header-cell-cushion',
  '.fc-timeline-slot-cushion',
  '.fc-timeline-slot-frame',
];
const DAY_GRID_CELL_CONTENT_SELECTORS = ['.fc-daygrid-day-top'];

/**
 * A FullCalendar `data-date` attribute is a `YYYY-MM-DD` string for dayGrid cells, and for
 * timeline slot labels is *usually* `YYYY-MM-DD` too but may carry a time suffix (e.g.
 * `YYYY-MM-DDT00:00:00`) depending on slot granularity. Slicing the first 10 characters keys
 * either shape to the same string `useProjectWeather`'s `Map<string, WeatherDay>` uses (`day.date`
 * is already `YYYY-MM-DD`) — this deliberately never goes through `new Date(...)`, which parses a
 * bare `YYYY-MM-DD` string as UTC midnight and can land on the wrong local calendar day.
 */
export const dateKeyFromAttr = (raw: string | null): string | null => (raw ? raw.slice(0, 10) : null);

/**
 * the Project view's (`resourceTimelineProject`) custom two-row header (`slotLabelFormat: [{month/year}, {day/weekday}]`
 * in the calendar's own view config) renders one `<tr class="fc-timeline-header-row">` per level,
 * each holding that level's `.fc-timeline-slot-label` cells as siblings. The coarser (month/year)
 * row is never the last row; the finest, date-bearing row — the only one a glyph belongs on — is
 * always `:last-child` among those `<tr>`s (verified against `@fullcalendar/timeline`'s
 * `TimelineHeaderRows`, which renders `cellRows.map((rowCells, rowLevel) => <tr>...)` with the
 * date-bearing granularity always the final entry). This generalizes the DidMount hooks' own
 * `arg.level === 0` check to a DOM-only signal, since a direct DOM query has no `level` value to
 * read. A single-row header (`resourceTimelineMonth`) has only one `.fc-timeline-header-row`, so
 * it is always `:last-child` and this never skips it.
 */
export const isCoarseTimelineHeaderRow = (cellEl: HTMLElement): boolean => {
  const row = cellEl.closest('tr.fc-timeline-header-row');
  if (!row || !row.parentElement) return false;
  const rows = row.parentElement.querySelectorAll(':scope > tr.fc-timeline-header-row');
  if (rows.length < 2) return false;
  return row !== rows[rows.length - 1];
};

const resolveGlyphContainer = (cellEl: HTMLElement, selectors: string[]): HTMLElement => {
  for (const selector of selectors) {
    const found = cellEl.querySelector(selector) as HTMLElement | null;
    if (found) return found;
  }
  return cellEl;
};

/**
 * Attaches (or clears) the weather glyph for one already-mounted slot-label/day-cell element,
 * keyed by a `YYYY-MM-DD` date string. Clearing always runs first — restructured (sprint 34
 * regression fix) from the original `attachWeatherGlyph`, which returned early before clearing
 * whenever there was no day, no condition, or an `UNAVAILABLE` row, so a stale glyph from a
 * previous fetch was never removed when new data made a date no longer render one.
 */
export const attachWeatherGlyphByKey = (
  cellEl: HTMLElement,
  dateKey: string,
  contentSelectors: string[],
  { weatherByDate, strings, dateLocale }: WeatherGlyphContext,
): void => {
  const container = resolveGlyphContainer(cellEl, contentSelectors);
  container.querySelectorAll('.zh-weather-wrapper').forEach((node) => node.remove());

  const day = weatherByDate.get(dateKey);
  if (!day) return;

  const tooltipText = buildWeatherTooltipText(day, strings, dateLocale, isCalendarToday(dateKey));
  const glyphHtml = renderWeatherGlyphHtml(day, tooltipText);
  if (!glyphHtml) return;

  const wrapper = document.createElement('span');
  wrapper.className = 'zh-weather-wrapper';
  wrapper.innerHTML = glyphHtml;
  container.appendChild(wrapper);
};

/**
 * Re-applies weather glyphs to every timeline slot-label / dayGrid cell already mounted inside
 * `root` — the calendar's own container element, scoped per project accordion (never `document`).
 * Call this whenever the weather data (or the strings/locale used to render it) changes; it is
 * safe to call repeatedly and never duplicates or stacks glyphs, since `attachWeatherGlyphByKey`
 * always clears first. Does not touch the `Calendar` instance itself (no `render()`, no
 * destroy/recreate) — glyphs are painted directly onto existing DOM nodes, so scroll position,
 * selection, and navigation state are untouched.
 */
export const applyWeatherGlyphs = (root: HTMLElement, view: string, ctx: WeatherGlyphContext): void => {
  root.querySelectorAll<HTMLElement>(TIMELINE_SLOT_LABEL_SELECTOR).forEach((cellEl) => {
    if (view === PROJECT_VIEW && isCoarseTimelineHeaderRow(cellEl)) return;
    const dateKey = dateKeyFromAttr(cellEl.getAttribute('data-date'));
    if (!dateKey) return;
    attachWeatherGlyphByKey(cellEl, dateKey, TIMELINE_SLOT_LABEL_CONTENT_SELECTORS, ctx);
  });

  root.querySelectorAll<HTMLElement>(DAY_GRID_CELL_SELECTOR).forEach((cellEl) => {
    const dateKey = dateKeyFromAttr(cellEl.getAttribute('data-date'));
    if (!dateKey) return;
    attachWeatherGlyphByKey(cellEl, dateKey, DAY_GRID_CELL_CONTENT_SELECTORS, ctx);
  });
};
